// src/components/Teacher/TeacherDashboard.tsx
//
// Replaces the isTeacher branch in Dashboard.tsx.
// In Dashboard.tsx add at top of isTeacher check:
//   if (isTeacher) return <TeacherDashboard />;
//
// ⚠️  Two schema assumptions — grep for "VERIFY" below.
 
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  collection, query, where, getDocs, getDoc, doc,
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import CreateAssignmentModal from './CreateAssignmentModal';
import ClassCreationModal from './ClassCreationModal';
 
// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  primary:            '#6a37d4',
  primaryLight:       '#ae8dff',
  primaryContainer:   '#ede8ff',
  secondary:          '#006947',
  secondaryContainer: '#d1fae5',
  tertiary:           '#6f5900',
  tertiaryContainer:  '#fed01b',
  surface:            '#f3f6ff',
  surfaceLow:         '#eaf1ff',
  surfaceHigh:        '#d6e3f9',
  surfaceHighest:     '#cfdef5',
  surfaceLowest:      '#ffffff',
  onBackground:       '#252f3c',
  onSurface:          '#252f3c',
  onSurfaceVariant:   '#525c6a',
  outlineVariant:     '#a4aebe',
  error:              '#b41340',
  errorContainer:     '#fde8ef',
};
 
// ── Types ─────────────────────────────────────────────────────────────────────
type FeedItemType = 'submission' | 'resubmission' | 'ai_flag';
type ActiveFilter = 'all' | 'pending_review' | 'needs_feedback' | 'past_due';
type SortOrder    = 'newest' | 'oldest';
 
interface FeedItem {
  id:             string;
  type:           FeedItemType;
  storyId:        string;
  studentId:      string;
  studentName:    string;
  classId:        string;
  className:      string;
  storyTitle:     string;
  timestamp:      any; // Firestore Timestamp
  assignmentId?:  string;
  assignmentTitle?: string;
  hasFeedback:    boolean;
  flagReason?:    string;
  isPastDue:      boolean;
}
 
interface Assignment {
  id:              string;
  title:           string;
  dueDate:         any;
  classId:         string;
  submissionCount: number;
  isPastDue:       boolean;
}
 
interface ClassRow {
  id:           string;
  name:         string;
  joinCode:     string;
  studentCount: number;
  assignments:  Assignment[]; // active (non-past-due) only
  expanded:     boolean;
}
 
// ── Helpers ───────────────────────────────────────────────────────────────────
function formatRelative(ts: any): string {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)  return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
 
function formatDueDate(ts: any): string {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
 
// ── Nav item (matches Dashboard.tsx pattern) ───────────────────────────────────
const NavItem: React.FC<{
  icon: string; label: string; active: boolean;
  onClick: () => void; badge?: string;
}> = ({ icon, label, active, onClick, badge }) => (
  <button
    onClick={onClick}
    style={{
      width: '100%', padding: '11px 16px',
      background: active ? C.primaryContainer : 'transparent',
      color: active ? C.primary : C.onSurfaceVariant,
      border: 'none', borderRadius: '14px',
      textAlign: 'left', fontSize: '0.875rem', fontWeight: '700',
      cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '12px',
      fontFamily: "'Plus Jakarta Sans', sans-serif", transition: 'all 0.15s',
    }}
    onMouseEnter={e => { if (!active) e.currentTarget.style.background = C.surfaceHigh; }}
    onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
  >
    <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{icon}</span>
    <span style={{ flex: 1 }}>{label}</span>
    {badge && (
      <span style={{
        background: C.error, color: '#fff',
        padding: '2px 7px', borderRadius: '9999px',
        fontSize: '0.65rem', fontWeight: '800',
      }}>
        {badge}
      </span>
    )}
  </button>
);
 
// ── Skeleton card (loading state) ─────────────────────────────────────────────
const SkeletonCard: React.FC = () => (
  <div style={{
    background: C.surfaceLowest, borderRadius: '16px', padding: '18px 20px',
    border: `1px solid ${C.surfaceHigh}`, height: '76px',
    animation: 'pulse 1.6s ease-in-out infinite',
  }} />
);
 
// ── Main component ────────────────────────────────────────────────────────────
const TeacherDashboard: React.FC = () => {
  const navigate  = useNavigate();
  const loc       = useLocation();
  const { currentUser } = useAuth();
  const username  = currentUser?.displayName || 'Teacher';
 
  const [isMobile,      setIsMobile]      = useState(window.innerWidth < 768);
  const [feedItems,     setFeedItems]     = useState<FeedItem[]>([]);
  const [classes,       setClasses]       = useState<ClassRow[]>([]);
  const [pastDueCount,  setPastDueCount]  = useState(0);
  const [loading,       setLoading]       = useState(true);
  const [activeFilter,  setActiveFilter]  = useState<ActiveFilter>('all');
  const [sortOrder,     setSortOrder]     = useState<SortOrder>('newest');
  const [filterClassId, setFilterClassId] = useState('all');
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showClassModal,      setShowClassModal]      = useState(false);
  const [modalClassId,        setModalClassId]        = useState('');
 
  const isActive = (path: string) => loc.pathname === path;
 
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
 
  // ── Data loading ───────────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      // 1. Teacher's classes
      const classSnap = await getDocs(
        query(collection(db, 'classes'), where('teacherId', '==', currentUser.uid))
      );
      const rawClasses = classSnap.docs.map(d => ({ id: d.id, ...d.data() } as any));
      const activeClasses = rawClasses.filter((c: any) => !c.archived);
      const classIds: string[] = activeClasses.map((c: any) => c.id);
      const classMap: Record<string, string> = {};
      activeClasses.forEach((c: any) => { classMap[c.id] = c.name; });
 
      if (classIds.length === 0) {
        setClasses([]);
        setFeedItems([]);
        setPastDueCount(0);
        setLoading(false);
        return;
      }
 
      // 2. All assignments (for sidebar + past-due counter)
      const now = new Date();
      const aSnap = await getDocs(
        query(collection(db, 'assignments'), where('teacherId', '==', currentUser.uid))
      );
      const allAssignments: Assignment[] = aSnap.docs.map(d => {
        const data = d.data() as any;
        const dueDateObj = data.dueDate?.toDate?.() ?? null;
        return {
          id:              d.id,
          title:           data.title || 'Untitled Assignment',
          dueDate:         data.dueDate,
          classId:         data.classId,
          // VERIFY: field name for submission count on your assignments documents
          submissionCount: data.submissionCount ?? 0,
          isPastDue:       dueDateObj ? dueDateObj < now : false,
        };
      });
 
      const activeAssignments = allAssignments.filter(a => !a.isPastDue);
      setPastDueCount(allAssignments.filter(a => a.isPastDue).length);
 
      // Build assignment lookup (all, including past-due — needed for isPastDue on feed items)
      const assignmentMap: Record<string, Assignment> = {};
      allAssignments.forEach(a => { assignmentMap[a.id] = a; });
 
      // Build class rows (sidebar)
      const classRows: ClassRow[] = activeClasses.map((c: any) => ({
        id:           c.id,
        name:         c.name,
        joinCode:     c.joinCode,
        studentCount: Object.keys(c.students || {}).length,
        assignments:  activeAssignments.filter(a => a.classId === c.id),
        expanded:     false,
      }));
      setClasses(classRows);
 
      // 3. Published stories across all classes (Firestore 'in' max 10 — chunk)
      const chunks: string[][] = [];
      for (let i = 0; i < classIds.length; i += 10) chunks.push(classIds.slice(i, i + 10));
 
      const storyDocs: any[] = [];
      for (const chunk of chunks) {
        const sSnap = await getDocs(
          query(
            collection(db, 'stories'),
            where('classId', 'in', chunk),
            where('status', '==', 'published')
          )
        );
        sSnap.docs.forEach(d => storyDocs.push({ id: d.id, ...d.data() }));
      }
 
      // 4. AI-flagged content
      // VERIFY: collection name + field names to match your ModerationInbox schema.
      // Common patterns: 'moderationFlags' | 'flaggedStories' | 'contentFlags'
      // Expected fields: storyId, classId, status ('pending'), reason/flagReason, flaggedAt/createdAt, userId/studentId
      let flagDocs: any[] = [];
      try {
        for (const chunk of chunks) {
          const fSnap = await getDocs(
            query(
              collection(db, 'moderationFlags'),
              where('classId', 'in', chunk),
              where('status', '==', 'pending')
            )
          );
          fSnap.docs.forEach(d => flagDocs.push({ id: d.id, ...d.data() }));
        }
      } catch {
        // Collection may not exist yet — safe to skip
      }
 
      // 5. Batch-fetch student display names
      const studentIds = Array.from(new Set([
        ...storyDocs.map((s: any) => s.userId),
        ...flagDocs.map((f: any) => f.userId || f.studentId),
      ].filter(Boolean)));
 
      const nameMap: Record<string, string> = {};
      await Promise.all(studentIds.map(async uid => {
        try {
          const uDoc = await getDoc(doc(db, 'users', uid));
          nameMap[uid] = uDoc.exists() ? (uDoc.data().displayName || 'Student') : 'Student';
        } catch {
          nameMap[uid] = 'Student';
        }
      }));
 
      // 6. Map stories → feed items
      const storyItems: FeedItem[] = storyDocs.map((s: any) => {
        const assignment = s.assignmentId ? assignmentMap[s.assignmentId] : undefined;
        // VERIFY: resubmission detection — adjust field names to your schema.
        // Common patterns: submissionCount > 1, or presence of resubmittedAt
        const isResub = (s.submissionCount != null && s.submissionCount > 1) || !!s.resubmittedAt;
        return {
          id:             `story-${s.id}`,
          type:           isResub ? 'resubmission' : 'submission',
          storyId:        s.id,
          studentId:      s.userId,
          studentName:    nameMap[s.userId] || 'Student',
          classId:        s.classId,
          className:      classMap[s.classId] || 'Class',
          storyTitle:     s.title || 'Untitled',
          timestamp:      s.resubmittedAt || s.submittedAt || s.publishedAt || s.createdAt,
          assignmentId:   s.assignmentId,
          assignmentTitle: assignment?.title,
          // VERIFY: field used to record "teacher has left feedback"
          hasFeedback:    !!(s.hasFeedback || s.teacherFeedback || s.feedbackCount),
          isPastDue:      assignment?.isPastDue ?? false,
        };
      });
 
      // 7. Map flags → feed items
      const flagItems: FeedItem[] = flagDocs.map((f: any) => ({
        id:          `flag-${f.id}`,
        type:        'ai_flag' as FeedItemType,
        storyId:     f.storyId,
        studentId:   f.userId || f.studentId,
        studentName: nameMap[f.userId || f.studentId] || 'Student',
        classId:     f.classId,
        className:   classMap[f.classId] || 'Class',
        storyTitle:  f.storyTitle || f.title || 'Untitled',
        timestamp:   f.flaggedAt || f.createdAt,
        hasFeedback: false,
        flagReason:  f.reason || f.flagReason,
        isPastDue:   false,
      }));
 
      setFeedItems([...storyItems, ...flagItems]);
    } catch (err) {
      console.error('TeacherDashboard: error loading data', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);
 
  useEffect(() => { loadAll(); }, [loadAll]);
 
  // ── Derived / filtered feed ────────────────────────────────────────────────
  const pendingReviewCount  = feedItems.filter(i => i.type === 'ai_flag').length;
  const needsFeedbackCount  = feedItems.filter(i => i.type !== 'ai_flag' && !i.hasFeedback).length;
 
  const filteredFeed = feedItems
    .filter(item => {
      if (activeFilter === 'pending_review') return item.type === 'ai_flag';
      if (activeFilter === 'needs_feedback') return item.type !== 'ai_flag' && !item.hasFeedback;
      if (activeFilter === 'past_due')       return item.isPastDue;
      return true;
    })
    .filter(item => filterClassId === 'all' || item.classId === filterClassId)
    .sort((a, b) => {
      const ta = a.timestamp?.toMillis?.() ?? 0;
      const tb = b.timestamp?.toMillis?.() ?? 0;
      return sortOrder === 'newest' ? tb - ta : ta - tb;
    });
 
  const toggleClass = (id: string) =>
    setClasses(prev => prev.map(c => c.id === id ? { ...c, expanded: !c.expanded } : c));
 
  const openAssignmentModal = (classId = '') => {
    setModalClassId(classId);
    setShowAssignmentModal(true);
  };
 
  // ── Counter card ──────────────────────────────────────────────────────────
  const CounterCard: React.FC<{
    filter: ActiveFilter; icon: string; label: string;
    count: number; accent: string; accentBg: string;
  }> = ({ filter, icon, label, count, accent, accentBg }) => {
    const on = activeFilter === filter;
    return (
      <button
        onClick={() => setActiveFilter(on ? 'all' : filter)}
        style={{
          background:  on ? accent : C.surfaceLowest,
          border:      `2px solid ${on ? accent : C.surfaceHigh}`,
          borderRadius: '18px', padding: '18px 22px',
          display: 'flex', alignItems: 'center', gap: '14px',
          cursor: 'pointer', textAlign: 'left', width: '100%',
          transition: 'all 0.15s', fontFamily: "'Plus Jakarta Sans', sans-serif",
          boxShadow: on ? `0 4px 20px ${accent}35` : '0 1px 4px rgba(0,0,0,0.04)',
        }}
        onMouseEnter={e => {
          if (!on) {
            e.currentTarget.style.borderColor = accent;
            e.currentTarget.style.background  = accentBg;
          }
        }}
        onMouseLeave={e => {
          if (!on) {
            e.currentTarget.style.borderColor = C.surfaceHigh;
            e.currentTarget.style.background  = C.surfaceLowest;
          }
        }}
      >
        <div style={{
          width: 46, height: 46, borderRadius: '13px', flexShrink: 0,
          background: on ? 'rgba(255,255,255,0.18)' : accentBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.25rem',
        }}>
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: "'Epilogue', sans-serif",
            fontSize: '1.9rem', fontWeight: '900', lineHeight: 1,
            color: on ? '#fff' : C.onBackground,
          }}>
            {count}
          </div>
          <div style={{
            fontSize: '0.72rem', fontWeight: '700', marginTop: '3px',
            textTransform: 'uppercase', letterSpacing: '0.6px',
            color: on ? 'rgba(255,255,255,0.82)' : C.onSurfaceVariant,
          }}>
            {label}
          </div>
        </div>
        {/* Live dot */}
        {count > 0 && (
          <div style={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            background: on ? 'rgba(255,255,255,0.55)' : accent,
          }} />
        )}
      </button>
    );
  };
 
  // ── Feed card ──────────────────────────────────────────────────────────────
  const FeedCard: React.FC<{ item: FeedItem }> = ({ item }) => {
    const cfg = {
      submission:   { icon: '📖', color: C.primary,  bg: C.primaryContainer,  label: 'New'          },
      resubmission: { icon: '🔄', color: C.tertiary, bg: C.tertiaryContainer, label: 'Resubmitted'  },
      ai_flag:      { icon: '🚩', color: C.error,    bg: C.errorContainer,    label: 'AI Flagged'   },
    }[item.type];
 
    const onAction = () => {
      if (item.type === 'ai_flag') {
        navigate('/teacher/moderation');        // → ModerationInbox
      } else {
        navigate(`/teacher/class/${item.classId}/story/${item.storyId}`); // → StoryViewer
      }
    };
 
    const actionLabel =
      item.type === 'ai_flag'    ? 'Review Flag'      :
      item.hasFeedback           ? 'View'             :
                                   'Leave Feedback';
 
    const actionBg =
      item.type === 'ai_flag'    ? C.error            :
      item.hasFeedback           ? C.surfaceHigh       :
                                   C.primary;
 
    const actionColor = item.hasFeedback ? C.onSurfaceVariant : '#fff';
 
    return (
      <div
        style={{
          background: C.surfaceLowest, borderRadius: '16px',
          padding: '15px 18px',
          border: `1px solid ${item.type === 'ai_flag' ? C.errorContainer : C.surfaceHigh}`,
          display: 'flex', alignItems: 'center', gap: '14px',
          boxShadow: item.type === 'ai_flag'
            ? `0 0 0 2px ${C.errorContainer}, 0 2px 8px rgba(0,0,0,0.04)`
            : '0 1px 4px rgba(0,0,0,0.04)',
          transition: 'box-shadow 0.15s, transform 0.15s',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 20px rgba(0,0,0,0.09)';
          (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLElement).style.boxShadow = item.type === 'ai_flag'
            ? `0 0 0 2px ${C.errorContainer}, 0 2px 8px rgba(0,0,0,0.04)`
            : '0 1px 4px rgba(0,0,0,0.04)';
          (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
        }}
      >
        {/* Type icon */}
        <div style={{
          width: 42, height: 42, borderRadius: '12px', flexShrink: 0,
          background: cfg.bg, display: 'flex',
          alignItems: 'center', justifyContent: 'center', fontSize: '1.1rem',
        }}>
          {cfg.icon}
        </div>
 
        {/* Meta */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '4px', flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: "'Epilogue', sans-serif", fontWeight: '800',
              fontSize: '0.875rem', color: C.onBackground,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              maxWidth: '220px',
            }}>
              {item.storyTitle}
            </span>
            <span style={{
              background: cfg.bg, color: cfg.color,
              padding: '2px 8px', borderRadius: '9999px',
              fontSize: '0.6rem', fontWeight: '800',
              textTransform: 'uppercase', letterSpacing: '0.5px', flexShrink: 0,
            }}>
              {cfg.label}
            </span>
          </div>
 
          <div style={{
            fontSize: '0.775rem', color: C.onSurfaceVariant,
            display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap',
          }}>
            <span>
              by <strong style={{ color: C.onBackground, fontWeight: '700' }}>{item.studentName}</strong>
            </span>
            <span style={{ color: C.outlineVariant }}>·</span>
            <span>{item.className}</span>
            {item.assignmentTitle && (
              <>
                <span style={{ color: C.outlineVariant }}>·</span>
                <span style={{ color: C.primary, fontWeight: '600' }}>
                  📋 {item.assignmentTitle}
                </span>
              </>
            )}
            {item.flagReason && (
              <>
                <span style={{ color: C.outlineVariant }}>·</span>
                <span style={{ color: C.error, fontWeight: '600' }}>{item.flagReason}</span>
              </>
            )}
            {item.isPastDue && !item.flagReason && (
              <>
                <span style={{ color: C.outlineVariant }}>·</span>
                <span style={{ color: C.tertiary, fontWeight: '600' }}>⚠️ Past due</span>
              </>
            )}
            <span style={{ color: C.outlineVariant }}>·</span>
            <span>{formatRelative(item.timestamp)}</span>
          </div>
        </div>
 
        {/* CTA */}
        <button
          onClick={onAction}
          style={{
            background: actionBg, color: actionColor,
            border: 'none', borderRadius: '9999px',
            padding: '8px 16px', fontSize: '0.775rem', fontWeight: '700',
            cursor: 'pointer', fontFamily: "'Epilogue', sans-serif",
            whiteSpace: 'nowrap', flexShrink: 0,
            transition: 'opacity 0.12s',
          }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.82'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
        >
          {actionLabel} →
        </button>
      </div>
    );
  };
 
  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: C.surface, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
 
      {/* ── Sidebar ─────────────────────────────────────────────────────── */}
      {!isMobile && (
        <div style={{
          position: 'fixed', top: 55, left: 0, bottom: 0, width: '220px',
          background: C.surfaceLow, borderRadius: '0 24px 24px 0',
          padding: '24px 12px', display: 'flex', flexDirection: 'column', gap: '4px',
          zIndex: 40, overflowY: 'auto', boxShadow: '4px 0 16px rgba(0,0,0,0.04)',
        }}>
          <NavItem icon="🏠" label="Dashboard"   active={isActive('/dashboard')}          onClick={() => navigate('/dashboard')} />
          <NavItem icon="👥" label="My Classes"   active={isActive('/teacher/classes')}    onClick={() => navigate('/teacher/classes')} />
          <NavItem icon="📋" label="Assignments"  active={isActive('/teacher/assignments')} onClick={() => navigate('/teacher/assignments')} />
          <NavItem icon="💬" label="Feedback"     active={isActive('/teacher/feedback')}   onClick={() => navigate('/teacher/feedback')} />
          {pendingReviewCount > 0 && (
            <NavItem
              icon="🚩" label="Moderation"
              active={isActive('/teacher/moderation')}
              onClick={() => navigate('/teacher/moderation')}
              badge={String(pendingReviewCount)}
            />
          )}
        </div>
      )}
 
      {/* ── Main content ────────────────────────────────────────────────── */}
      <div style={{
        marginLeft: isMobile ? 0 : '220px',
        padding: isMobile ? '20px 16px 80px' : '40px 40px',
        minHeight: '100vh', boxSizing: 'border-box',
      }}>
 
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
          gap: '16px', marginBottom: '28px', flexWrap: 'wrap',
        }}>
          <div>
            <h1 style={{
              fontFamily: "'Epilogue', sans-serif",
              fontSize: isMobile ? '1.8rem' : '2.4rem',
              fontWeight: '900', color: C.onBackground, margin: '0 0 6px 0', lineHeight: '1.1',
            }}>
              Good{new Date().getHours() < 12 ? ' morning' : new Date().getHours() < 17 ? ' afternoon' : ' evening'},{' '}
              <span style={{ color: C.primary, fontStyle: 'italic' }}>{username}.</span>
            </h1>
            <p style={{ fontSize: '0.9rem', color: C.onSurfaceVariant, margin: 0 }}>
              {loading
                ? 'Loading your classes…'
                : filteredFeed.length === 0 && activeFilter === 'all'
                  ? 'All caught up — no new activity.'
                  : `${filteredFeed.length} item${filteredFeed.length !== 1 ? 's' : ''} need${filteredFeed.length === 1 ? 's' : ''} your attention.`
              }
            </p>
          </div>
          <button
            onClick={() => openAssignmentModal()}
            style={{
              background: `linear-gradient(135deg, ${C.primary}, ${C.primaryLight})`,
              color: '#fff', border: 'none', borderRadius: '9999px',
              padding: '11px 24px', fontSize: '0.875rem', fontWeight: '700',
              cursor: 'pointer', fontFamily: "'Epilogue', sans-serif",
              boxShadow: `0 4px 14px ${C.primary}40`,
              display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap',
            }}
          >
            ➕ New Assignment
          </button>
        </div>
 
        {/* Counter row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '12px', marginBottom: '28px',
        }}>
          <CounterCard
            filter="pending_review" icon="🚩" label="Pending Review"
            count={pendingReviewCount} accent={C.error} accentBg={C.errorContainer}
          />
          <CounterCard
            filter="needs_feedback" icon="💬" label="Needs Feedback"
            count={needsFeedbackCount} accent={C.primary} accentBg={C.primaryContainer}
          />
          <CounterCard
            filter="past_due" icon="⚠️" label="Past Due"
            count={pastDueCount} accent={C.tertiary} accentBg={C.tertiaryContainer}
          />
        </div>
 
        {/* Two-column body: feed + assignments sidebar */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 296px',
          gap: '24px', alignItems: 'start',
        }}>
 
          {/* ── Activity feed ──────────────────────────────────────────── */}
          <div>
            {/* Sort + filter bar */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '10px', marginBottom: '14px', flexWrap: 'wrap',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 style={{
                  fontFamily: "'Epilogue', sans-serif", fontWeight: '900',
                  fontSize: '1.1rem', color: C.onBackground, margin: 0,
                }}>
                  Activity Feed
                </h2>
                {activeFilter !== 'all' && (
                  <button
                    onClick={() => setActiveFilter('all')}
                    style={{
                      background: C.surfaceHigh, border: 'none', borderRadius: '9999px',
                      padding: '3px 10px', fontSize: '0.7rem', fontWeight: '700',
                      color: C.onSurfaceVariant, cursor: 'pointer',
                    }}
                  >
                    ✕ Clear filter
                  </button>
                )}
              </div>
 
              <div style={{ display: 'flex', gap: '8px' }}>
                <select
                  value={sortOrder}
                  onChange={e => setSortOrder(e.target.value as SortOrder)}
                  style={{
                    background: C.surfaceLowest, border: `1px solid ${C.surfaceHigh}`,
                    borderRadius: '9999px', padding: '6px 13px',
                    fontSize: '0.775rem', fontWeight: '600', color: C.onBackground,
                    cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", outline: 'none',
                  }}
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                </select>
 
                <select
                  value={filterClassId}
                  onChange={e => setFilterClassId(e.target.value)}
                  style={{
                    background: C.surfaceLowest, border: `1px solid ${C.surfaceHigh}`,
                    borderRadius: '9999px', padding: '6px 13px',
                    fontSize: '0.775rem', fontWeight: '600', color: C.onBackground,
                    cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif", outline: 'none',
                  }}
                >
                  <option value="all">All classes</option>
                  {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
 
            {/* Feed list */}
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <SkeletonCard /><SkeletonCard /><SkeletonCard />
              </div>
            ) : filteredFeed.length === 0 ? (
              <div style={{
                background: C.surfaceLowest, borderRadius: '20px', padding: '48px 32px',
                textAlign: 'center', border: `1px solid ${C.surfaceHigh}`,
              }}>
                <div style={{ fontSize: '2.2rem', marginBottom: '10px' }}>
                  {activeFilter === 'all' ? '📭' : '✅'}
                </div>
                <div style={{
                  fontFamily: "'Epilogue', sans-serif", fontWeight: '800',
                  fontSize: '1rem', color: C.onBackground, marginBottom: '6px',
                }}>
                  {activeFilter === 'all' ? 'No activity yet' : 'All clear!'}
                </div>
                <div style={{ fontSize: '0.85rem', color: C.onSurfaceVariant }}>
                  {activeFilter === 'all'
                    ? 'New submissions and flags will appear here as students write.'
                    : 'Nothing in this category right now.'}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {filteredFeed.map(item => <FeedCard key={item.id} item={item} />)}
              </div>
            )}
          </div>
 
          {/* ── Assignments sidebar ─────────────────────────────────────── */}
          <div style={{ position: isMobile ? 'static' : 'sticky', top: '80px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: '12px',
            }}>
              <h2 style={{
                fontFamily: "'Epilogue', sans-serif", fontWeight: '900',
                fontSize: '1rem', color: C.onBackground, margin: 0,
              }}>
                Active Assignments
              </h2>
              <button
                onClick={() => openAssignmentModal()}
                style={{
                  background: C.primaryContainer, color: C.primary,
                  border: 'none', borderRadius: '9999px',
                  padding: '5px 12px', fontSize: '0.7rem', fontWeight: '800',
                  cursor: 'pointer', fontFamily: "'Epilogue', sans-serif",
                }}
              >
                ➕ New
              </button>
            </div>
 
            {loading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[1, 2].map(i => (
                  <div key={i} style={{ background: C.surfaceLowest, borderRadius: '14px', height: '48px', border: `1px solid ${C.surfaceHigh}`, opacity: 0.5 }} />
                ))}
              </div>
            ) : classes.length === 0 ? (
              <div style={{
                background: C.surfaceLowest, borderRadius: '16px', padding: '20px',
                textAlign: 'center', border: `1px solid ${C.surfaceHigh}`,
              }}>
                <div style={{ fontSize: '0.82rem', color: C.onSurfaceVariant, marginBottom: '10px' }}>
                  No classes yet.
                </div>
                <button
                  onClick={() => setShowClassModal(true)}
                  style={{
                    background: C.primary, color: '#fff', border: 'none', borderRadius: '9999px',
                    padding: '7px 16px', fontSize: '0.775rem', fontWeight: '700', cursor: 'pointer',
                    fontFamily: "'Epilogue', sans-serif",
                  }}
                >
                  Create Class
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {classes.map(cls => (
                  <div key={cls.id} style={{
                    background: C.surfaceLowest, borderRadius: '14px',
                    border: `1px solid ${C.surfaceHigh}`, overflow: 'hidden',
                  }}>
                    {/* Class header */}
                    <button
                      onClick={() => toggleClass(cls.id)}
                      style={{
                        width: '100%', padding: '12px 14px',
                        background: 'none', border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: '8px',
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                      }}
                    >
                      <span style={{
                        fontSize: '0.55rem', color: cls.expanded ? C.primary : C.onSurfaceVariant,
                        display: 'inline-block', flexShrink: 0,
                        transform: cls.expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                        transition: 'transform 0.18s',
                      }}>
                        ▶
                      </span>
                      <span style={{
                        flex: 1, fontWeight: '700', fontSize: '0.82rem',
                        color: C.onBackground, textAlign: 'left',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>
                        {cls.name}
                      </span>
                      <span style={{
                        background: cls.assignments.length > 0 ? C.primaryContainer : C.surfaceHigh,
                        color: cls.assignments.length > 0 ? C.primary : C.onSurfaceVariant,
                        padding: '2px 7px', borderRadius: '9999px',
                        fontSize: '0.62rem', fontWeight: '800', flexShrink: 0,
                      }}>
                        {cls.assignments.length}
                      </span>
                    </button>
 
                    {/* Expanded: assignment list */}
                    {cls.expanded && (
                      <div style={{ borderTop: `1px solid ${C.surfaceHigh}`, padding: '8px' }}>
                        {cls.assignments.length === 0 ? (
                          <div style={{
                            padding: '10px', fontSize: '0.775rem',
                            color: C.onSurfaceVariant, textAlign: 'center',
                          }}>
                            No active assignments
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            {cls.assignments.map(a => (
                              <div
                                key={a.id}
                                onClick={() => navigate(`/teacher/class/${a.classId}/assignment/${a.id}`)}
                                style={{
                                  padding: '10px 11px', borderRadius: '10px',
                                  background: C.surfaceLow, cursor: 'pointer',
                                  transition: 'background 0.12s',
                                }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = C.surfaceHigh; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = C.surfaceLow; }}
                              >
                                <div style={{
                                  fontWeight: '700', fontSize: '0.8rem',
                                  color: C.onBackground, marginBottom: '5px', lineHeight: '1.3',
                                }}>
                                  {a.title}
                                </div>
                                <div style={{
                                  display: 'flex', alignItems: 'center',
                                  justifyContent: 'space-between',
                                }}>
                                  <span style={{ fontSize: '0.7rem', color: C.onSurfaceVariant }}>
                                    Due {formatDueDate(a.dueDate)}
                                  </span>
                                  <span style={{ fontSize: '0.7rem', color: C.primary, fontWeight: '700' }}>
                                    {a.submissionCount} submitted
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
 
                        {/* Add assignment to this class */}
                        <button
                          onClick={() => openAssignmentModal(cls.id)}
                          style={{
                            width: '100%', marginTop: '5px',
                            border: `1.5px dashed ${C.outlineVariant}`, background: 'transparent',
                            borderRadius: '10px', padding: '7px',
                            fontSize: '0.7rem', fontWeight: '700',
                            color: C.onSurfaceVariant, cursor: 'pointer',
                            fontFamily: "'Epilogue', sans-serif", transition: 'all 0.12s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = C.outlineVariant; e.currentTarget.style.color = C.onSurfaceVariant; }}
                        >
                          ➕ Add to {cls.name}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
 
            {/* Manage classes link */}
            <button
              onClick={() => navigate('/teacher/classes')}
              style={{
                width: '100%', marginTop: '10px', background: 'none',
                border: `1.5px solid ${C.outlineVariant}`, borderRadius: '12px',
                padding: '9px', fontSize: '0.775rem', fontWeight: '700',
                color: C.onSurfaceVariant, cursor: 'pointer',
                fontFamily: "'Epilogue', sans-serif", transition: 'all 0.12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.outlineVariant; e.currentTarget.style.color = C.onSurfaceVariant; }}
            >
              👥 Manage All Classes →
            </button>
 
            {/* Shortcut links row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', marginTop: '6px' }}>
              {[
                { label: '📊 Feedback',    path: '/teacher/feedback'    },
                { label: '🚩 Moderation',  path: '/teacher/moderation'  },
              ].map(({ label, path }) => (
                <button
                  key={path}
                  onClick={() => navigate(path)}
                  style={{
                    background: 'none', border: `1.5px solid ${C.outlineVariant}`,
                    borderRadius: '12px', padding: '8px 6px',
                    fontSize: '0.72rem', fontWeight: '700',
                    color: C.onSurfaceVariant, cursor: 'pointer',
                    fontFamily: "'Epilogue', sans-serif", transition: 'all 0.12s',
                    textAlign: 'center',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.primary; e.currentTarget.style.color = C.primary; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.outlineVariant; e.currentTarget.style.color = C.onSurfaceVariant; }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
 
      {/* ── Modals ────────────────────────────────────────────────────────── */}
      <CreateAssignmentModal
        isOpen={showAssignmentModal}
        onClose={() => { setShowAssignmentModal(false); setModalClassId(''); }}
        classId={modalClassId}
        className={classes.find(c => c.id === modalClassId)?.name || 'General'}
        onAssignmentCreated={() => { setShowAssignmentModal(false); setModalClassId(''); loadAll(); }}
      />
      {showClassModal && (
        <ClassCreationModal
          onClose={() => setShowClassModal(false)}
          onClassCreated={() => { setShowClassModal(false); loadAll(); }}
        />
      )}
 
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.5; }
          50%       { opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};
 
export default TeacherDashboard;